// ─────────────────────────────────────────────────────────────────────────────
// CoApp Design System — Theme
// ─────────────────────────────────────────────────────────────────────────────
/// <reference path="./theme.d.ts" />
//
// Two themes (light + dark) built from a single token source. Custom CoApp
// tokens live in a `coapp` namespace inside the MUI theme so they don't
// collide with MUI's standard palette slots. TypeScript augmentation in
// `theme.d.ts` makes `theme.coapp.*` typed and discoverable.
//
// Brand: CoApp teal (#02D5AB) → blue (#0A7FA8) gradient.
// Typography: Work Sans, weights 300/400/500/600/700.
// Source of truth for token derivation: design/prototype/rule-builder.html
// (CSS variables) + design/gap-analysis.md.
//
// ─────────────────────────────────────────────────────────────────────────────

import type { PaletteMode, PaletteOptions } from "@mui/material/styles";
import { createTheme } from "@mui/material/styles";
import { createElement } from "react";

// Brand-gradient checkbox icons. Imported here so the theme's MuiCheckbox
// override can install them as the default icon set — every <Checkbox/>
// instance in the app picks up the gradient-filled checked state.
// Note: use React.createElement instead of `<UncheckedIcon/>` here for the
// defaultProps assignment below.
import { CheckedIcon, IndeterminateIcon, UncheckedIcon } from "./components/global/components/CheckboxIcons";

// ── Raw tokens ───────────────────────────────────────────────────────────────
//
// The single source of truth. Both themes derive from these. If a value is
// hard-coded anywhere below the tokens block, it's a mistake.
// ─────────────────────────────────────────────────────────────────────────────

const tokens = {
	// ─ Brand ──
	teal: {
		50:  "#E5FBF6",
		100: "#B8F4E5",
		200: "#7AE8CC",
		300: "#33DEC0",
		400: "#02D5AB", // brand primary
		500: "#02C49C",
		600: "#00B899", // prototype's --teal-deep
		700: "#008B5A",
		800: "#066848",
		900: "#054E37"
	},
	blue: {
		50:  "#E6F1F7",
		100: "#BFDDEC",
		200: "#80B8D8",
		300: "#4D96C5",
		400: "#1F7BB1",
		500: "#0A7FA8", // prototype's --teal-start (gradient anchor)
		600: "#08658A",
		700: "#054C68",
		800: "#033649",
		900: "#022734"
	},
	// ─ Status ──
	red: {
		50:  "#FEE7E5",
		100: "#FBC9C5",
		200: "#F49891",
		300: "#EC665E",
		400: "#E03030",
		500: "#CC2C2A",
		600: "#A82321",
		700: "#7E1A19",
		800: "#561110",
		900: "#330A0A"
	},
	amber: {
		50:  "#FFF7E6",
		100: "#FCEABE",
		200: "#F7D478",
		300: "#F5C020",
		400: "#E0AA00",
		500: "#C29200",
		600: "#A07900",
		700: "#7A5C00",
		800: "#553F00",
		900: "#332600"
	},
	green: {
		50:  "#E6F4EA",
		100: "#C2E5CD",
		200: "#86CB9D",
		300: "#4FB071",
		400: "#27ae60",
		500: "#1E8E4D",
		600: "#176F3C",
		700: "#10522C",
		800: "#0A361D",
		900: "#062012"
	},
	purple: {
		50:  "#EFEAFB",
		100: "#D4C5F4",
		200: "#A88BE9",
		300: "#7B52E0",
		400: "#6033CB",
		500: "#492AAC",
		600: "#352084",
		700: "#23165C",
		800: "#150D38",
		900: "#0A0620"
	},
	orange: {
		50:  "#FEEDE2",
		100: "#FBD2BB",
		200: "#F7A876",
		300: "#F38440",
		400: "#F06C28",
		500: "#D75A1A",
		600: "#AE4612",
		700: "#84330D",
		800: "#5A2208",
		900: "#331305"
	},
	// ─ Neutral ──
	gray: {
		50:  "#F7F9FA",
		100: "#EEF1F4",
		200: "#D9DEE3",
		300: "#B8C0C8",
		400: "#8C9AA6",
		500: "#647582",
		600: "#475662",
		700: "#2F3B45",
		800: "#1A222B",
		900: "#0D1218"
	},
	// ─ Surface (light) ──
	light: {
		bg:        "#F0F4F8", // page background
		panel:     "#E6EDF5", // sidebar / secondary panel
		surface:   "#FFFFFF", // cards / primary surface
		surface2:  "#F5F8FC", // hover / nested surface
		border:    "rgba(0,0,0,0.09)",
		borderSub: "rgba(0,0,0,0.05)",
		text:      "#0D1F30",
		muted:     "rgba(13,31,48,0.52)",
		subtle:    "rgba(13,31,48,0.32)",
		tableRow:  "#FFFFFF"  // grids should read as pure white surfaces
	},
	// ─ Surface (dark) ──
	dark: {
		bg:        "#060F1A",
		panel:     "#081824",
		surface:   "#0B1F30",
		surface2:  "#0D2540",
		border:    "rgba(255,255,255,0.10)",
		borderSub: "rgba(255,255,255,0.06)",
		text:      "#FFFFFF",
		muted:     "rgba(255,255,255,0.52)",
		subtle:    "rgba(255,255,255,0.30)",
		tableRow:  "#0D2540"
	},
	// ─ Spacing scale (multiplier of theme.spacing(1) = 8px) ──
	// Use as `theme.spacing(n)`. Listed here for reference / documentation.
	// 0 = 0, 0.5 = 4, 1 = 8, 1.5 = 12, 2 = 16, 3 = 24, 4 = 32, 6 = 48, 8 = 64
	// ─ Radius ──
	radius: {
		xs:   6,
		sm:   8,
		md:   12,
		lg:   16,
		xl:   22,
		pill: 100
	},
	// ─ Shadow ──
	shadow: {
		card:  "0 4px 16px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.08)",
		hover: "0 8px 28px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.12)",
		float: "0 24px 64px rgba(0,0,0,0.24), 0 0 0 1px rgba(0,0,0,0.06)"
	},
	// ─ Motion ──
	motion: {
		fast:    "0.12s cubic-bezier(0.4, 0, 0.2, 1)",
		default: "0.18s cubic-bezier(0.4, 0, 0.2, 1)",
		spring:  "0.32s cubic-bezier(0.34, 1.56, 0.64, 1)",
		smooth:  "0.24s cubic-bezier(0.22, 1, 0.36, 1)"
	},
	// ─ Gradient palette ──
	// Primary brand gradient (teal → deep blue) plus 5 additional category
	// gradients used to color icons, accent rails, and tinted backgrounds for
	// node tiles, status badges, and category chips. Each gradient has a
	// matching `*Solid` mid-tone token used for borders and text where a
	// gradient can't be applied (icons inherit currentColor by default).
	gradient: {
		primary: "linear-gradient(135deg, #02D5AB 0%, #0A7FA8 100%)",
		purple:  "linear-gradient(135deg, #D63AD9 0%, #8C30E0 100%)",
		blue:    "linear-gradient(135deg, #5BA0FF 0%, #2C66E0 100%)",
		red:     "linear-gradient(135deg, #E94136 0%, #B81E15 100%)",
		orange:  "linear-gradient(135deg, #FF7A3C 0%, #EE3A2D 100%)",
		yellow:  "linear-gradient(135deg, #FFD93B 0%, #F2A93B 100%)"
	},
	gradientSolid: {
		primary: "#06B097",
		purple:  "#A434D9",
		blue:    "#3F77E8",
		red:     "#D32E26",
		orange:  "#F35A35",
		yellow:  "#F5BB35"
	}
};

// ── Per-node-type colors (rule builder canvas) ──────────────────────────────
//
// Lifted from prototype's NODE_CFG. Each node type renders with its own
// color, soft tinted background, and gradient. Reaction Location is the
// brand teal (production currently renders it purple — see gap-analysis.md).
// ─────────────────────────────────────────────────────────────────────────────

// Per-node-type colors mapped to the new 6-gradient palette.
//   teal  → reactionlocation, sort, assignment   (entry, ordering, set-value)
//   blue  → query, dbvar, decision               (data + branch)
//   red   → reaction, clearreaction, stop        (alerts, ends)
//   purple → loop, simplevar                     (iteration, simple var)
//   orange → filter, formulavar                  (transform / compute)
//   yellow → reserved for future use (warnings)
const nodeColors = {
	reactionlocation: { color: tokens.gradientSolid.primary, bg: "rgba(2,213,171,0.15)",  gradient: tokens.gradient.primary },
	query:            { color: tokens.gradientSolid.blue,    bg: "rgba(63,119,232,0.14)", gradient: tokens.gradient.blue },
	loop:             { color: tokens.gradientSolid.purple,  bg: "rgba(164,52,217,0.14)", gradient: tokens.gradient.purple },
	decision:         { color: tokens.gradientSolid.blue,    bg: "rgba(63,119,232,0.14)", gradient: tokens.gradient.blue },
	filter:           { color: tokens.gradientSolid.orange,  bg: "rgba(243,90,53,0.14)",  gradient: tokens.gradient.orange },
	sort:             { color: tokens.gradientSolid.primary, bg: "rgba(2,213,171,0.12)",  gradient: tokens.gradient.primary },
	assignment:       { color: tokens.gradientSolid.primary, bg: "rgba(2,213,171,0.12)",  gradient: tokens.gradient.primary },
	reaction:         { color: tokens.gradientSolid.red,     bg: "rgba(211,46,38,0.14)",  gradient: tokens.gradient.red },
	clearreaction:    { color: tokens.gradientSolid.red,     bg: "rgba(211,46,38,0.10)",  gradient: tokens.gradient.red },
	stop:             { color: tokens.gradientSolid.red,     bg: "rgba(211,46,38,0.12)",  gradient: tokens.gradient.red },
	simplevar:        { color: tokens.gradientSolid.purple,  bg: "rgba(164,52,217,0.10)", gradient: tokens.gradient.purple },
	formulavar:       { color: tokens.gradientSolid.orange,  bg: "rgba(243,90,53,0.14)",  gradient: tokens.gradient.orange },
	dbvar:            { color: tokens.gradientSolid.blue,    bg: "rgba(63,119,232,0.14)", gradient: tokens.gradient.blue }
};

// ── Typography ──────────────────────────────────────────────────────────────
//
// Work Sans ramp. h2 + h5 are filled in (gap in legacy theme).
// Custom variants (h3_5, body3, subheader, etc.) preserved for backwards
// compatibility but should be migrated to standard variants over time.
// ─────────────────────────────────────────────────────────────────────────────

const typography = {
	fontFamily: "\"Work Sans\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", sans-serif",
	h1:         { fontSize: "2.25rem", lineHeight: 1.2,  fontWeight: 600, letterSpacing: "-0.02em" },
	h2:         { fontSize: "1.75rem", lineHeight: 1.25, fontWeight: 600, letterSpacing: "-0.01em" },
	h3:         { fontSize: "1.5rem",  lineHeight: 1.3,  fontWeight: 600, letterSpacing: "-0.005em" },
	h3_5:       { fontSize: "1.3rem",  lineHeight: 1.35, fontWeight: 500, letterSpacing: "0.005em" },
	h4:         { fontSize: "1.125rem", lineHeight: 1.4,  fontWeight: 500, letterSpacing: "0.01em" },
	h5:         { fontSize: "1rem",    lineHeight: 1.5,  fontWeight: 600, letterSpacing: "0.01em" },
	h6:         { fontSize: "0.9375rem", lineHeight: 1.5,  fontWeight: 600, letterSpacing: "0.02em" },
	subtitle1:  { fontSize: "1rem",    lineHeight: 1.5,  fontWeight: 500 },
	subtitle2:  { fontSize: "0.875rem", lineHeight: 1.5,  fontWeight: 500 },
	body1:      { fontSize: "1rem",    lineHeight: 1.5,  fontWeight: 400 },
	body2:      { fontSize: "0.875rem", lineHeight: 1.5,  fontWeight: 400 },
	body3:      { fontSize: "0.75rem", lineHeight: 1.4,  fontWeight: 400 },
	caption:    { fontSize: "0.75rem", lineHeight: 1.4,  fontWeight: 400 },
	overline:   { fontSize: "0.6875rem", lineHeight: 1.3, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" },
	button:     { fontSize: "0.875rem", lineHeight: 1.5,  fontWeight: 600, letterSpacing: "0.02em", textTransform: "none" },
	subheader:  { fontSize: "0.875rem", lineHeight: 1.7,  fontWeight: 500, letterSpacing: "0.01em" },
	menuItem:     { fontSize: "1rem",     lineHeight: 1.5, fontWeight: 400, letterSpacing: "0.01em" },
	menuItemDense:{ fontSize: "0.875rem", lineHeight: 1.5, fontWeight: 400, letterSpacing: "0.01em" },
	buttonSmall:  { fontSize: "0.8125rem", lineHeight: 1.4, fontWeight: 600, letterSpacing: "0.03em" }
};

// ── Breakpoints ─────────────────────────────────────────────────────────────
const breakpoints = {
	values: { xs: 0, sm: 600, md: 800, lg: 1400, xl: 1536, xxl: 1920 }
};

// ── Light theme ─────────────────────────────────────────────────────────────

// ── Legacy palette shims ────────────────────────────────────────────────────
//
// Existing callsites (e.g. `theme.palette.icon.red`, `palette.supporting.*`,
// `palette.background.tableRow`) reference custom keys from the old theme.
// These shims keep them working until callsites migrate to the new namespaces
// in Phase 2. DO NOT add new code that depends on these.
// ─────────────────────────────────────────────────────────────────────────────

const legacyShims = (mode: PaletteMode) => {
	const isDark = mode === "dark";
	const surface = isDark ? tokens.dark : tokens.light;
	return {
		// theme.palette.icon.{main,grey,red,green}
		icon: {
			main:  tokens.green[300],
			grey:  surface.muted,
			red:   tokens.red[400],
			green: tokens.green[700]
		},
		// theme.palette.supporting.* (SCIM wizard, etc.)
		supporting: {
			green:               tokens.green[400],
			red:                 tokens.red[400],
			yellow:              tokens.amber[400],
			scimStepCompleted:   tokens.teal[400],
			scimStepActive:      tokens.teal[700],
			scimStepInactive:    "#FFFFFF"
		},
		// theme.palette.black_states.*
		black_states: {
			main:           "#000000",
			hover:          "rgba(0,0,0,0.04)",
			selected:       "rgba(0,0,0,0.08)",
			focus:          "rgba(0,0,0,0.12)",
			focusVisible:   "rgba(0,0,0,0.30)",
			outlinedBorder: "rgba(0,0,0,0.50)",
			icon:           "rgba(0,0,0,0.54)"
		},
		// theme.palette.white_states.*
		white_states: {
			main:           "#FFFFFF",
			hover:          "rgba(255,255,255,0.04)",
			selected:       "rgba(255,255,255,0.08)",
			focus:          "rgba(255,255,255,0.12)",
			focusVisible:   "rgba(255,255,255,0.30)",
			outlinedBorder: "rgba(255,255,255,0.50)",
			disabled:       "#F5F5F5"
		},
		// theme.palette.paperTitle.main (legacy magenta accent)
		paperTitle: { main: "#F50057" }
	};
};

// Legacy `_states` sub-object generator. Every palette role used to expose
// `_states.{hover,selected,focus,focusVisible,outlinedBorder,disabled,filledContainer}`.
// Modern code should use theme.palette.action.* — these shims keep the old
// callsites working until they migrate.
type LegacyStateOverrides = Partial<{
	hover: string;
	selected: string;
	focus: string;
	focusVisible: string;
	outlinedBorder: string;
	disabled: string;
	filledContainer: string;
}>;

const legacyStates = (hex: string, opts: LegacyStateOverrides = {}) => ({
	hover:           opts.hover           ?? `${hex}1A`,
	selected:        opts.selected        ?? `${hex}29`,
	focus:           opts.focus           ?? `${hex}3D`,
	focusVisible:    opts.focusVisible    ?? `${hex}66`,
	outlinedBorder:  opts.outlinedBorder  ?? `${hex}80`,
	disabled:        opts.disabled        ?? "#F5F5F5",
	filledContainer: opts.filledContainer ?? `${hex}1A`
});

const lightPalette = {
	mode: "light" as const,
	primary: {
		main:     tokens.teal[400],
		light:    tokens.teal[300],
		dark:     tokens.teal[600],
		contrastText: "#FFFFFF",
		_states:  legacyStates(tokens.teal[400])
	},
	secondary: {
		main:     tokens.blue[500],
		light:    tokens.blue[300],
		dark:     tokens.blue[700],
		contrastText: "#FFFFFF",
		_states:  legacyStates(tokens.blue[500])
	},
	error: {
		main:     tokens.red[400],
		light:    tokens.red[300],
		dark:     tokens.red[500],
		contrastText: "#FFFFFF",
		_states: {
			...legacyStates(tokens.red[400]),
			killSwitchHover:      "rgba(233,80,75,0.15)",
			killSwitchSelected:   "rgba(233,80,75,0.20)",
			killSwitchActiveBase: "rgba(233,80,75,0.10)"
		}
	},
	warning: {
		main:     tokens.amber[400],
		light:    tokens.amber[300],
		dark:     tokens.amber[500],
		contrastText: "#1A222B",
		_states:  legacyStates(tokens.amber[400])
	},
	info: {
		main:     tokens.blue[500],
		light:    tokens.blue[300],
		dark:     tokens.blue[700],
		contrastText: "#FFFFFF",
		_states:  legacyStates(tokens.blue[500])
	},
	success: {
		main:     tokens.green[400],
		light:    tokens.green[300],
		dark:     tokens.green[500],
		contrastText: "#FFFFFF",
		_states:  legacyStates(tokens.green[400])
	},
	text: {
		primary:      tokens.light.text,
		secondary:    tokens.light.muted,
		disabled:     tokens.light.subtle,
		// Legacy alias used in some styled components.
		coappPrimary: tokens.teal[400],
		_states: {
			hover:        "rgba(0,0,0,0.04)",
			selected:     "rgba(0,0,0,0.08)",
			focus:        "rgba(0,0,0,0.12)",
			focusVisible: "rgba(0,0,0,0.30)"
		}
	},
	background: {
		default:  tokens.light.bg,
		paper:    tokens.light.surface,
		// Legacy aliases.
		tableRow: tokens.light.tableRow,
		"paper-elevation-0": tokens.light.surface,
		"paper-elevation-1": tokens.light.surface
	},
	divider: tokens.light.border,
	action: {
		hover:           "rgba(2, 213, 171, 0.06)",
		selected:        "rgba(2, 213, 171, 0.12)",
		focus:           "rgba(2, 213, 171, 0.16)",
		disabled:        tokens.light.subtle,
		disabledBackground: "rgba(0,0,0,0.06)"
	},
	grey: tokens.gray,
	...legacyShims("light")
};

const darkPalette = {
	mode: "dark" as const,
	primary: {
		main:     tokens.teal[400],
		light:    tokens.teal[300],
		dark:     tokens.teal[600],
		contrastText: tokens.dark.bg,
		_states:  legacyStates(tokens.teal[400])
	},
	secondary: {
		main:     tokens.blue[400],
		light:    tokens.blue[300],
		dark:     tokens.blue[600],
		contrastText: "#FFFFFF",
		_states:  legacyStates(tokens.blue[400])
	},
	error: {
		main:     tokens.red[400],
		light:    tokens.red[300],
		dark:     tokens.red[500],
		contrastText: "#FFFFFF",
		_states: {
			...legacyStates(tokens.red[400]),
			killSwitchHover:      "rgba(233,80,75,0.15)",
			killSwitchSelected:   "rgba(233,80,75,0.20)",
			killSwitchActiveBase: "rgba(233,80,75,0.10)"
		}
	},
	warning: {
		main:     tokens.amber[300],
		light:    tokens.amber[200],
		dark:     tokens.amber[500],
		contrastText: tokens.dark.bg,
		_states:  legacyStates(tokens.amber[300])
	},
	info: {
		main:     tokens.blue[400],
		light:    tokens.blue[300],
		dark:     tokens.blue[600],
		contrastText: "#FFFFFF",
		_states:  legacyStates(tokens.blue[400])
	},
	success: {
		main:     tokens.green[400],
		light:    tokens.green[300],
		dark:     tokens.green[500],
		contrastText: "#FFFFFF",
		_states:  legacyStates(tokens.green[400])
	},
	text: {
		primary:      tokens.dark.text,
		secondary:    tokens.dark.muted,
		disabled:     tokens.dark.subtle,
		coappPrimary: tokens.teal[400],
		_states: {
			hover:        "rgba(255,255,255,0.04)",
			selected:     "rgba(255,255,255,0.08)",
			focus:        "rgba(255,255,255,0.12)",
			focusVisible: "rgba(255,255,255,0.30)"
		}
	},
	background: {
		default:  tokens.dark.bg,
		paper:    tokens.dark.surface,
		tableRow: tokens.dark.tableRow,
		"paper-elevation-0": tokens.dark.surface,
		"paper-elevation-1": tokens.dark.surface2
	},
	divider: tokens.dark.border,
	action: {
		hover:           "rgba(2, 213, 171, 0.10)",
		selected:        "rgba(2, 213, 171, 0.18)",
		focus:           "rgba(2, 213, 171, 0.22)",
		disabled:        tokens.dark.subtle,
		disabledBackground: "rgba(255,255,255,0.08)"
	},
	grey: tokens.gray,
	...legacyShims("dark")
};

// ── coapp namespace (typed in theme.d.ts) ───────────────────────────────────
//
// Custom CoApp tokens live here. `theme.coapp.*` is fully typed via module
// augmentation so editor autocomplete and lint catches typos.
// ─────────────────────────────────────────────────────────────────────────────

const coappLight = {
	mode: "light" as const,
	tokens,                      // expose raw scales for ad-hoc use
	nodeColors,                  // per-node-type colors for the rule builder
	gradient: tokens.gradient,
	gradientSolid: tokens.gradientSolid,
	radius:   tokens.radius,
	shadow:   tokens.shadow,
	motion:   tokens.motion,
	// Interactive text colors that meet WCAG AA contrast on white. The bright
	// brand teal (#02D5AB ≈ 2.1:1) and the gradient mid-tones (~3.4:1) BOTH
	// fail AA for body text. Use these darker variants whenever a brand color
	// is the *text* (links, labels, list items, active nav). Backgrounds,
	// chips, icons, and gradient *surfaces* can still use the bright tokens.
	text: {
		interactive:        "#057A6D",  // primary teal — 4.6:1 on white ✓
		interactiveHover:   "#03665B",  // hover/active darker step
		interactiveBlue:    "#1F58CC",  // 4.7:1 on white ✓
		interactivePurple:  "#7A1FBF",  // 5.3:1 on white ✓
		interactiveRed:     "#B81E15",  // 5.7:1 on white ✓
		interactiveOrange:  "#C03515"   // 5.6:1 on white ✓
	},
	surface: {
		bg:        tokens.light.bg,
		panel:     tokens.light.panel,
		surface:   tokens.light.surface,
		surface2:  tokens.light.surface2,
		border:    tokens.light.border,
		borderSub: tokens.light.borderSub,
		tableRow:  tokens.light.tableRow
	},
	// Feature-specific tokens. Add sparingly; prefer derivation from `tokens`.
	killSwitch: {
		hover:       "rgba(233,80,75,0.15)",
		selected:    "rgba(233,80,75,0.20)",
		activeBase:  "rgba(233,80,75,0.10)"
	}
};

const coappDark = {
	...coappLight,
	mode: "dark" as const,
	surface: {
		bg:        tokens.dark.bg,
		panel:     tokens.dark.panel,
		surface:   tokens.dark.surface,
		surface2:  tokens.dark.surface2,
		border:    tokens.dark.border,
		borderSub: tokens.dark.borderSub,
		tableRow:  tokens.dark.tableRow
	}
};

// ── MUI component overrides ─────────────────────────────────────────────────
//
// Single source of truth for the visual identity of every MUI primitive.
// Adding a new override here propagates to every page.
// ─────────────────────────────────────────────────────────────────────────────

function buildComponents(
	palette: typeof lightPalette | typeof darkPalette,
	coapp: typeof coappLight | typeof coappDark
) {
	const isDark = palette.mode === "dark";
	return {
		MuiCssBaseline: {
			styleOverrides: {
				// Stretch the document/root to full viewport height so any
				// descendant using `position: absolute; height: calc(100% ...)`
				// (e.g. CoAppDataGridMain, RuleBuilder canvas wrapper) resolves
				// against a real height instead of collapsing to 0.
				"html, body, #root": {
					height: "100%",
					margin: 0,
					padding: 0
				},
				// Expose every brand token as a CSS custom property on :root
				// so styled-components, raw CSS, and SVG fills can all reference
				// `var(--coapp-gradient-primary)` instead of hardcoding the hex
				// string. Single source of truth for the design tokens.
				":root": {
					"--coapp-color-teal":            coapp.tokens.teal[400],
					"--coapp-color-teal-deep":       coapp.tokens.teal[600],
					"--coapp-color-teal-light":      coapp.tokens.teal[300],
					"--coapp-color-blue":            coapp.tokens.blue[500],
					"--coapp-color-blue-deep":       coapp.tokens.blue[700],
					"--coapp-color-red":             coapp.tokens.red[400],
					"--coapp-color-amber":           coapp.tokens.amber[400],
					"--coapp-color-green":           coapp.tokens.green[400],
					"--coapp-color-purple":          coapp.tokens.purple[300],
					"--coapp-color-orange":          coapp.tokens.orange[400],

					"--coapp-gradient-primary":      coapp.gradient.primary,
					"--coapp-gradient-purple":       coapp.gradient.purple,
					"--coapp-gradient-blue":         coapp.gradient.blue,
					"--coapp-gradient-red":          coapp.gradient.red,
					"--coapp-gradient-orange":       coapp.gradient.orange,
					"--coapp-gradient-yellow":       coapp.gradient.yellow,
					"--coapp-solid-primary":         coapp.gradientSolid.primary,
					"--coapp-solid-purple":          coapp.gradientSolid.purple,
					"--coapp-solid-blue":            coapp.gradientSolid.blue,
					"--coapp-solid-red":             coapp.gradientSolid.red,
					"--coapp-solid-orange":          coapp.gradientSolid.orange,
					"--coapp-solid-yellow":          coapp.gradientSolid.yellow,

					// Accessible text colors (4.5:1+ on white, WCAG AA compliant).
					"--coapp-text-interactive":         coapp.text.interactive,
					"--coapp-text-interactive-hover":   coapp.text.interactiveHover,
					"--coapp-text-interactive-blue":    coapp.text.interactiveBlue,
					"--coapp-text-interactive-purple":  coapp.text.interactivePurple,
					"--coapp-text-interactive-red":     coapp.text.interactiveRed,
					"--coapp-text-interactive-orange":  coapp.text.interactiveOrange,

					// ─ Descope / Vaadin Lumo shadow-DOM theming ──────────
					// These have to live on :root because the Descope flow
					// renders inside Web Components with their own shadow
					// roots — only :root-level custom properties cascade in.
					"--lumo-primary-color":          "#02D5AB",
					"--lumo-primary-text-color":     "#02D5AB",
					"--lumo-primary-color-50pct":    "rgba(2, 213, 171, 0.5)",
					"--lumo-primary-color-10pct":    "rgba(2, 213, 171, 0.1)",
					"--lumo-primary-contrast-color": "#FFFFFF",
					"--lumo-body-text-color":        "rgba(13, 31, 48, 0.85)",
					"--lumo-secondary-text-color":   "rgba(13, 31, 48, 0.55)",
					"--lumo-tertiary-text-color":    "rgba(13, 31, 48, 0.40)",
					"--lumo-border-radius-s":        "6px",
					"--lumo-border-radius-m":        "8px",
					"--lumo-font-family":            "\"Work Sans\", -apple-system, BlinkMacSystemFont, sans-serif",
					"--lumo-font-weight-strong":     "600",
					"--lumo-size-m":                 "44px",
					// Descope button properties — covers BOTH the generic
					// `--descope-button-*` family AND the more-specific
					// `--descope-button-primary-host-*` family that primary
					// CTAs (the indigo "Continue" button) actually consume in
					// the v2 web-component. Setting both is harmless and
					// future-proofs against the SDK renaming.
					"--descope-button-host-background-color":              coapp.gradient.primary,
					"--descope-button-host-color":                         "#FFFFFF",
					"--descope-button-host-border-radius":                 "100px",
					"--descope-button-host-border":                        "0",
					"--descope-button-host-font-weight":                   "600",
					"--descope-button-host-font-size":                     "15px",
					"--descope-button-host-padding":                       "10px 20px",
					"--descope-button-host-min-height":                    "44px",
					"--descope-button-host-box-shadow":                    "0 4px 12px rgba(2, 213, 171, 0.20)",
					"--descope-button-hover-host-background-color":        coapp.gradient.primary,
					"--descope-button-hover-host-filter":                  "brightness(1.08)",
					// Primary-CTA-specific overrides.
					"--descope-button-primary-host-background-color":      coapp.gradient.primary,
					"--descope-button-primary-host-color":                 "#FFFFFF",
					"--descope-button-primary-host-border":                "0",
					"--descope-button-primary-host-border-radius":         "100px",
					"--descope-button-primary-hover-host-background-color": coapp.gradient.primary,
					"--descope-button-primary-hover-host-filter":          "brightness(1.08)",
					"--descope-button-primary-active-host-background-color": coapp.gradient.primary,
					// Belt + suspenders — Vaadin Lumo button parts under the
					// hood read from these too; keeping them set ensures the
					// brand teal wins regardless of which selector resolves.
					"--vaadin-button-background":                          coapp.gradient.primary,
					"--vaadin-button-primary-background":                  coapp.gradient.primary,
					"--vaadin-button-primary-text-color":                  "#FFFFFF",

					"--coapp-radius-xs":             `${coapp.radius.xs}px`,
					"--coapp-radius-sm":             `${coapp.radius.sm}px`,
					"--coapp-radius-md":             `${coapp.radius.md}px`,
					"--coapp-radius-lg":             `${coapp.radius.lg}px`,
					"--coapp-radius-xl":             `${coapp.radius.xl}px`,
					"--coapp-radius-pill":           `${coapp.radius.pill}px`,

					"--coapp-shadow-card":           coapp.shadow.card,
					"--coapp-shadow-hover":          coapp.shadow.hover,
					"--coapp-shadow-float":          coapp.shadow.float,

					"--coapp-motion-fast":           coapp.motion.fast,
					"--coapp-motion-default":        coapp.motion.default,
					"--coapp-motion-spring":         coapp.motion.spring,
					"--coapp-motion-smooth":         coapp.motion.smooth,

					"--coapp-surface-bg":            coapp.surface.bg,
					"--coapp-surface-panel":         coapp.surface.panel,
					"--coapp-surface-card":          coapp.surface.surface,
					"--coapp-surface-card-2":        coapp.surface.surface2,
					"--coapp-surface-border":        coapp.surface.border,
					"--coapp-surface-border-sub":    coapp.surface.borderSub,
					"--coapp-surface-table-row":     coapp.surface.tableRow
				},
				body: {
					// Pure white app surface — no soft-grey backdrop. The grid,
					// sidebar, and any uncovered gaps all share this canvas.
					backgroundColor: isDark ? coapp.surface.bg : "#FFFFFF",
					color: palette.text.primary,
					WebkitFontSmoothing: "antialiased",
					MozOsxFontSmoothing: "grayscale"
				}
			}
		},
		// AppBar — flat, hairline border-bottom. No shadow so the page
		// reads as one continuous surface from header → sidebar → content.
		// Height stays at 80 to match CoAppHeader's HEADER_HEIGHT constant
		// and CoAppDataGridMain's `top: 80; height: calc(100% - 80px)`.
		MuiAppBar: {
			styleOverrides: {
				root: {
					backgroundColor: "#FFFFFF",
					color: palette.text.primary,
					boxShadow: "none",
					borderBottom: "1px solid rgba(13, 31, 48, 0.06)",
					height: 80
				}
			}
		},
		MuiButton: {
			defaultProps: { disableElevation: true },
			styleOverrides: {
				root: {
					borderRadius: coapp.radius.pill,
					textTransform: "none",
					fontWeight: 600,
					letterSpacing: "0.02em",
					transition: `background ${coapp.motion.default}, box-shadow ${coapp.motion.default}`,
					// Keyboard-only focus ring (Tab/Shift-Tab). Uses
					// :focus-visible so click focus stays clean.
					"&.Mui-focusVisible": {
						outline: "none",
						boxShadow: `0 0 0 3px ${palette.primary.main}40`
					}
				},
				containedPrimary: {
					background: coapp.gradient.primary,
					color: "#FFFFFF",
					"&:hover": {
						background: coapp.gradient.primary,
						boxShadow: coapp.shadow.hover,
						filter: "brightness(1.05)"
					},
					"&.Mui-focusVisible": {
						boxShadow: `${coapp.shadow.hover}, 0 0 0 3px ${palette.primary.main}55`
					},
					"&.Mui-disabled": { background: isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.10)", color: palette.text.disabled }
				},
				outlinedPrimary: {
					borderColor: palette.primary.main,
					"&:hover": { borderColor: palette.primary.dark, background: palette.action.hover }
				}
			}
		},
		MuiIconButton: {
			styleOverrides: {
				root: {
					":hover": { backgroundColor: palette.action.hover },
					// Keyboard-only focus ring around the icon button.
					"&.Mui-focusVisible": {
						outline: "none",
						boxShadow: `0 0 0 3px ${palette.primary.main}40`
					}
				}
			}
		},
		MuiTextField: {
			defaultProps: { variant: "outlined", size: "small" } as const,
			styleOverrides: {
				root: {
					"& .MuiOutlinedInput-root": {
						borderRadius: coapp.radius.sm,
						backgroundColor: coapp.surface.surface,
						"& fieldset":           { borderColor: coapp.surface.border },
						"&:hover fieldset":     { borderColor: palette.primary.main },
						"&.Mui-focused fieldset": { borderColor: palette.primary.main, borderWidth: 2 }
					}
				}
			}
		},
		// MuiOutlinedInput — also styles bare <Select variant="outlined">,
		// <Autocomplete>, and <OutlinedInput> instances that don't go through
		// <TextField>. Brand teal hover + focus, soft border, 8px radius.
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					borderRadius: coapp.radius.sm,
					backgroundColor: coapp.surface.surface,
					fontSize: 14,
					fontWeight: 500,
					transition: "border-color 0.18s, box-shadow 0.18s",
					"& .MuiOutlinedInput-notchedOutline": {
						borderColor: coapp.surface.border,
						transition: "border-color 0.18s"
					},
					"&:hover .MuiOutlinedInput-notchedOutline": {
						borderColor: "rgba(2, 213, 171, 0.5)"
					},
					"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
						borderColor: palette.primary.main,
						borderWidth: 1.5
					},
					"&.Mui-focused": {
						boxShadow: `0 0 0 2px ${palette.primary.main}14`
					},
					// Keyboard-only focus ring (Tab/Shift-Tab) — slightly more
					// prominent than click focus for accessibility, but still subtle.
					"&.Mui-focusVisible .MuiOutlinedInput-notchedOutline, &:has(input:focus-visible) .MuiOutlinedInput-notchedOutline": {
						borderColor: palette.primary.main,
						borderWidth: 1.5
					},
					"&.Mui-focusVisible, &:has(input:focus-visible)": {
						boxShadow: `0 0 0 2px ${palette.primary.main}1f`
					},
					"&.Mui-error .MuiOutlinedInput-notchedOutline": {
						borderColor: palette.error.main
					},
					"&.Mui-disabled": {
						background: isDark ? "rgba(255,255,255,0.04)" : "rgba(13, 31, 48, 0.03)"
					}
				}
			}
		},
		MuiInputLabel: {
			styleOverrides: {
				root: {
					fontSize: 13,
					fontWeight: 500,
					color: "rgba(13, 31, 48, 0.55)",
					"&.Mui-focused": { color: palette.primary.main },
					"&.Mui-error":   { color: palette.error.main }
				}
			}
		},
		MuiSelect: {
			styleOverrides: {
				select: {
					fontSize: 14,
					fontWeight: 500,
					padding: "10px 32px 10px 12px"
				},
				icon: {
					color: "rgba(13, 31, 48, 0.55)",
					transition: "color 0.18s"
				}
			}
		},
		MuiFormHelperText: {
			styleOverrides: {
				root: {
					marginLeft: 0,
					marginTop: 4,
					fontSize: 11,
					fontWeight: 500,
					letterSpacing: "0.01em",
					color: "rgba(13, 31, 48, 0.45)",
					"&.Mui-error": { color: palette.error.main }
				}
			}
		},
		MuiChip: {
			styleOverrides: {
				root: {
					borderRadius: coapp.radius.pill,
					"& .MuiChip-label": {
						maxWidth: 200,
						textOverflow: "ellipsis",
						"&:hover": { cursor: "default" }
					}
				}
			}
		},
		// Checkbox — uses custom gradient-filled icons (CheckedIcon /
		// IndeterminateIcon) so the brand teal-to-blue gradient is applied
		// uniformly across every <Checkbox/> in the app. No per-instance
		// overrides needed.
		MuiCheckbox: {
			defaultProps: {
				disableRipple: true,
				icon:              createElement(UncheckedIcon),
				checkedIcon:       createElement(CheckedIcon),
				indeterminateIcon: createElement(IndeterminateIcon)
			},
			styleOverrides: {
				root: {
					padding: 6,
					color: palette.text.secondary,
					"&.Mui-checked": { color: palette.primary.main },
					"&:hover": { background: "rgba(2, 213, 171, 0.06)" }
				}
			}
		},
		// Switch — when ON, the THUMB is painted with the full brand gradient
		// and the TRACK is a soft tinted background of the same gradient. The
		// off state keeps a desaturated dark track so contrast stays WCAG AA
		// without relying on hue alone.
		MuiSwitch: {
			defaultProps: { disableRipple: true },
			styleOverrides: {
				root: {
					width: 38,
					height: 22,
					padding: 0,
					display: "inline-flex",
					alignItems: "center",
					"& .MuiSwitch-switchBase": {
						padding: 2,
						top: 1,
						left: 1,
						"&.Mui-checked": {
							transform: "translateX(16px)",
							color: "transparent",
							"& .MuiSwitch-thumb": {
								// Full brand gradient on the thumb + a soft
								// shadow that picks up the teal end of the
								// ramp.
								background: "linear-gradient(135deg, #02D5AB 0%, #0A7FA8 100%)",
								boxShadow: "0 1px 3px rgba(2, 213, 171, 0.45), 0 1px 1px rgba(13, 31, 48, 0.10)"
							},
							"& + .MuiSwitch-track": {
								// Lighter alpha-version of the gradient as
								// the track background. White inner shadow
								// keeps the thumb readable.
								background: "linear-gradient(135deg, rgba(2, 213, 171, 0.22) 0%, rgba(10, 127, 168, 0.22) 100%)",
								opacity: 1,
								border: "1px solid rgba(2, 213, 171, 0.40)"
							}
						},
						"&.Mui-focusVisible .MuiSwitch-thumb": {
							boxShadow: "0 0 0 4px rgba(2, 213, 171, 0.18)"
						},
						"&.Mui-disabled .MuiSwitch-thumb": {
							color: "#FFFFFF",
							opacity: 0.6
						},
						"&.Mui-disabled + .MuiSwitch-track": {
							opacity: 0.4
						}
					},
					"& .MuiSwitch-thumb": {
						width: 16,
						height: 16,
						background: "#FFFFFF",
						boxShadow: "0 1px 3px rgba(13, 31, 48, 0.18), 0 1px 1px rgba(13, 31, 48, 0.10)",
						transition: "background 0.18s ease"
					},
					"& .MuiSwitch-track": {
						borderRadius: 999,
						background: "rgba(13, 31, 48, 0.32)",
						border: "1px solid rgba(13, 31, 48, 0.10)",
						opacity: 1,
						transition: "background 0.18s ease, border-color 0.18s ease"
					}
				}
			}
		},
		// Radio — checked state shows a gradient-filled inner dot. The unchecked
		// outline stays neutral. We avoid :nth-of-type() because MUI's Radio
		// renders both the unchecked and checked SVGs as siblings; the runtime
		// switches between them via opacity/transform, so positional selectors
		// are brittle across MUI minor versions.
		MuiRadio: {
			defaultProps: { disableRipple: true },
			styleOverrides: {
				root: {
					padding: 6,
					color: "rgba(13, 31, 48, 0.32)",
					"&.Mui-checked": {
						// Solid teal as a fallback for any browser that
						// can't resolve the SVG <linearGradient> reference.
						color: "#02D5AB",
						// Gradient fill on the SVG paths — both children
						// inherit it; only the visible one paints.
						"& .MuiSvgIcon-root path": {
							fill: "url(#coapp-grad-primary)"
						}
					},
					"&:hover": { background: "rgba(2, 213, 171, 0.06)" }
				}
			}
		},
		MuiDataGrid: {
			// Column pinning is disabled for every grid in the app: removes the
			// "Pin to left/right" column-menu items and ignores any per-grid
			// initialState.pinnedColumns / pinnedColumns props that remain.
			//
			// Autosizing: every grid sizes its columns to their content on mount
			// so long values (rule names, descriptions, etc.) aren't truncated.
			// `expand` grows columns to fill the grid when content is narrow;
			// `includeOutliers` + `includeHeaders` size to the widest cell/header.
			defaultProps: {
				disableColumnPinning: true,
				autosizeOnMount: true,
				autosizeOptions: {
					includeHeaders: true,
					includeOutliers: true,
					expand: true
				}
			},
			styleOverrides: {
				root: {
					border: `1px solid ${coapp.surface.border}`,
					borderRadius: coapp.radius.sm,
					"& .MuiDataGrid-toolbarContainer": {
						backgroundColor: coapp.surface.surface,
						color: palette.text.secondary,
						"& .MuiButton-text": { color: palette.text.secondary }
					},
					"& .MuiDataGrid-pinnedColumnHeaders": { backgroundColor: coapp.surface.surface },
					"& .MuiDataGrid-pinnedColumns":       { backgroundColor: coapp.surface.surface }
				}
			}
		},
		MuiMenuItem: {
			styleOverrides: {
				root: ({ ownerState }: { ownerState: { variant?: string } }) => {
					const base = { ":hover": { backgroundColor: palette.action.hover } };
					if (ownerState?.variant === "destructive") {
						return { ...base, color: palette.error.main };
					}
					return base;
				}
			}
		},
		MuiTooltip: {
			styleOverrides: {
				tooltip: {
					backgroundColor: isDark ? coapp.surface.surface2 : coapp.tokens.gray[800],
					color: "#FFFFFF",
					fontSize: "0.75rem",
					fontWeight: 500,
					padding: "6px 10px",
					borderRadius: coapp.radius.xs
				},
				arrow: {
					color: isDark ? coapp.surface.surface2 : coapp.tokens.gray[800]
				}
			}
		}
	};
}

// ── Build themes ────────────────────────────────────────────────────────────

export const lightModeTheme = createTheme({
	palette: lightPalette as PaletteOptions,
	typography,
	breakpoints,
	shape: { borderRadius: tokens.radius.sm },
	components: buildComponents(lightPalette, coappLight),
	coapp: coappLight,
});

export const darkModeTheme = createTheme({
	palette: darkPalette as PaletteOptions,
	typography,
	breakpoints,
	shape: { borderRadius: tokens.radius.sm },
	components: buildComponents(darkPalette, coappDark),
	coapp: coappDark,
});

// Re-export the raw building blocks so stories / tools can consume tokens
// directly without needing the full MUI theme.
export { coappDark,coappLight, nodeColors, tokens };

// Default export is the light theme — preserves drop-in compatibility with
// `import { lightModeTheme } from './theme'` callers.
export default lightModeTheme;
